/**
 * Where a record's page lives.
 *
 * In shared/ so the server can build the same links the app does: the morning
 * email and the reminder notifications used to guess (every company reminder
 * pointed at `/companys/<id>`, which matches no route).
 */
import type { TaskLinkRef } from "./taskLinks";

/** One destination for every company category, including legacy links. */
export function companyHref(id: string, category?: string | null): string {
  const routes: Record<string, string> = {
    "Buyer Network": "buyers",
    Client: "clients",
    "Sell-Side": "clients",
    Prospects: "prospects",
    "Capital Providers": "capital-providers",
  };
  return `/${routes[category ?? ""] ?? "companies"}/${encodeURIComponent(id)}`;
}

/** A person's page. It redirects to /coi/:id on its own when they are marked as a COI. */
export function contactHref(id: string): string {
  return `/contacts/${encodeURIComponent(id)}`;
}

export function taskLinkHref(l: TaskLinkRef & { category?: string | null }): string {
  return l.kind === "company" ? companyHref(l.id, l.category) : contactHref(l.id);
}

/** The other kinds a reminder's generic pair can name, with the target each had before. */
const OTHER_REMINDER_ROUTES: Record<string, string> = {
  deal: "deals",
  email: "emails",
  activity: "activities",
  task: "tasks",
};

/**
 * The page a fired reminder opens. Companies and people go to their own page;
 * the other known kinds keep the target they had. `remind_entity_type` is free
 * text from POST /api/reminders and this path goes into an email href, so an
 * unknown kind goes to /tasks and every id is encoded.
 */
export function reminderEntityPath(
  entityType: string | null,
  entityId: string | null,
  companyCategory: string | null,
): string {
  if (!entityType || !entityId) return "/tasks";
  if (entityType === "company") return companyHref(entityId, companyCategory);
  if (entityType === "contact") return contactHref(entityId);
  const route = OTHER_REMINDER_ROUTES[entityType];
  return route ? `/${route}/${encodeURIComponent(entityId)}` : "/tasks";
}
